import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Settings, Save, AlertCircle, CheckCircle, Plus, Trash2, RefreshCcw, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API, toast } from '@/App';
import { getAuthToken } from '@/utils/authSession';

const defaultConfig = {
  brevo_api_key: '',
  brevo_sender_name: '',
  brevo_sender_email: '',
  brevo_enabled: false,
  fcm_enabled: false,
  fcm_project_id: '',
  fcm_client_email: '',
  fcm_private_key: '',
  fcm_sender_id: '',
  google_maps_api_key: '',
  google_maps_enabled: false,
};

const SECRET_FIELD_KEYS = ['brevo_api_key', 'fcm_private_key', 'google_maps_api_key', 'stripe_public_key', 'stripe_secret_key'];
const defaultSecretMetadata = SECRET_FIELD_KEYS.reduce((accumulator, fieldName) => {
  accumulator[fieldName] = { configured: false, masked_value: '' };
  return accumulator;
}, {});

const APIConfiguration = () => {
  const [config, setConfig] = useState(defaultConfig);
  const [customProcessors, setCustomProcessors] = useState([]);
  const [showAddProcessor, setShowAddProcessor] = useState(false);
  const [newProcessor, setNewProcessor] = useState({
    name: '',
    slug: '',
    api_keys: {},
  });
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [secretMetadata, setSecretMetadata] = useState(defaultSecretMetadata);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overview, setOverview] = useState(null);
  const [processingJobs, setProcessingJobs] = useState(false);
  const [providerTestLoading, setProviderTestLoading] = useState({ brevo: false });
  const [providerDiagnostics, setProviderDiagnostics] = useState({ brevo: null });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/admin/api-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const responseData = response.data || {};
      const nextSecretMetadata = { ...defaultSecretMetadata, ...(responseData.secret_fields || {}) };
      const nextConfig = { ...defaultConfig, ...responseData };
      SECRET_FIELD_KEYS.forEach((fieldName) => {
        nextConfig[fieldName] = '';
      });
      delete nextConfig.secret_fields;
      setSecretMetadata(nextSecretMetadata);
      setConfig(nextConfig);
      await loadAutomationOverview(token);
    } catch (error) {
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadAutomationOverview = async (tokenOverride = null) => {
    try {
      const token = tokenOverride || getAuthToken();
      const response = await axios.get(`${API}/admin/automations/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(response.data);
    } catch (error) {
      setOverview(null);
    }
  };

  const runProviderTest = async (provider) => {
    setProviderTestLoading((prev) => ({ ...prev, [provider]: true }));
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API}/admin/providers/${provider}/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const diagnostic = response.data?.data || null;
      setProviderDiagnostics((prev) => ({ ...prev, [provider]: diagnostic }));
      if (diagnostic?.overall_status === 'ok') {
        toast.success(diagnostic.summary || response.data?.message || `Test ${provider} réussi`);
      } else if (diagnostic?.overall_status === 'blocked' || diagnostic?.overall_status === 'partial') {
        toast.warning(diagnostic.summary || `Diagnostic ${provider} à vérifier`);
      } else {
        toast.error(diagnostic?.summary || `Diagnostic ${provider} en échec`);
      }
      await loadAutomationOverview(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Échec du test ${provider}`);
    } finally {
      setProviderTestLoading((prev) => ({ ...prev, [provider]: false }));
    }
  };

  const processAutomationJobsNow = async () => {
    setProcessingJobs(true);
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API}/admin/automations/process`,
        null,
        {
          params: { limit: 25 },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const processed = response.data?.data?.picked ?? 0;
      toast.success(`Jobs traités : ${processed}`);
      await loadAutomationOverview(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec du traitement des jobs');
    } finally {
      setProcessingJobs(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = getAuthToken();
      const payload = { ...config };
      SECRET_FIELD_KEYS.forEach((fieldName) => {
        const normalizedValue = String(payload[fieldName] || '').trim();
        if (!normalizedValue) {
          delete payload[fieldName];
          return;
        }
        payload[fieldName] = normalizedValue;
      });
      await axios.patch(`${API}/admin/api-config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Configuration enregistrée avec succès');
      await loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const getSecretFieldMeta = (fieldName) => secretMetadata[fieldName] || defaultSecretMetadata[fieldName] || { configured: false, masked_value: '' };
  const isSecretConfigured = (fieldName) => Boolean(getSecretFieldMeta(fieldName).configured || String(config[fieldName] || '').trim());
  const getSecretHelperText = (fieldName, fallbackText) => {
    const fieldMeta = getSecretFieldMeta(fieldName);
    if (fieldMeta.configured && fieldMeta.masked_value) {
      return `Clé déjà configurée côté backend : ${fieldMeta.masked_value}. Laissez vide pour la conserver.`;
    }
    return fallbackText || 'Laissez vide pour conserver la valeur existante.';
  };

  const formatDiagnosticDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString('fr-FR');
  };

  const getDiagnosticBadgeClasses = (status) => {
    if (status === 'ok') return 'bg-green-50 border-green-200 text-green-800';
    if (status === 'blocked' || status === 'partial') return 'bg-amber-50 border-amber-200 text-amber-800';
    return 'bg-red-50 border-red-200 text-red-800';
  };

  const getCapabilityStateLabel = (capability) => (capability?.success ? 'OK' : 'À vérifier');

  const addCustomKey = () => {
    if (!newKeyName || !newKeyValue) return;
    setNewProcessor((prev) => ({
      ...prev,
      api_keys: {
        ...prev.api_keys,
        [newKeyName]: newKeyValue,
      },
    }));
    setNewKeyName('');
    setNewKeyValue('');
  };

  const removeCustomKey = (keyName) => {
    const updatedKeys = { ...newProcessor.api_keys };
    delete updatedKeys[keyName];
    setNewProcessor((prev) => ({ ...prev, api_keys: updatedKeys }));
  };

  const saveCustomProcessor = () => {
    if (!newProcessor.name || !newProcessor.slug) {
      toast.error('Nom et slug sont obligatoires');
      return;
    }
    setCustomProcessors((prev) => [...prev, { ...newProcessor, id: Date.now() }]);
    setNewProcessor({ name: '', slug: '', api_keys: {} });
    setShowAddProcessor(false);
    toast.success('Processeur ajouté avec succès');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Configuration API</h2>
          <p className="text-gray-600 mt-1">
            Gérez les providers externes de façon structurée, avec Brevo au centre des emails et FCM prêt pour les futures notifications push.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {overview && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">État emails et automatisations</CardTitle>
            <CardDescription>
              Vue d'ensemble des providers actifs et de la file de jobs sur les dernières 24 heures.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="p-3 rounded border bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500">Email transactionnel</p>
                <p className="text-sm font-semibold">
                  {(overview.providers?.transactional || 'none') === 'brevo' ? 'Brevo' : 'Non configuré'}
                </p>
              </div>
              <div className="p-3 rounded border bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500">CRM</p>
                <p className="text-sm font-semibold">
                  {(overview.providers?.crm || 'none') === 'brevo' ? 'Brevo' : 'Non configuré'}
                </p>
              </div>
              <div className="p-3 rounded border bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500">Marketing</p>
                <p className="text-sm font-semibold">
                  {(overview.providers?.marketing || 'none') === 'brevo' ? 'Brevo' : 'Non configuré'}
                </p>
              </div>
              <div className="p-3 rounded border bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500">Push mobile</p>
                <p className="text-sm font-semibold">
                  {(overview.providers?.push || 'none') === 'fcm' ? 'FCM prêt' : 'Préparation'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {overview.push_device_tokens_active ?? overview.providers?.fcm?.active_device_tokens ?? 0} token(s) actifs
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Envoyés 24h : <span className="font-semibold text-gray-900">{overview.push_dispatch_last_24h?.sent ?? 0}</span>
                  {' '}• Échecs 24h : <span className="font-semibold text-gray-900">{overview.push_dispatch_last_24h?.failed ?? 0}</span>
                </p>
              </div>
              <div className="p-3 rounded border bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500">Échecs email (24h)</p>
                <p className="text-sm font-semibold">{overview.email_dispatch_failures_last_24h ?? 0}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded border">
                <p className="text-xs text-gray-500">En attente</p>
                <p className="font-semibold">{overview.jobs?.pending ?? 0}</p>
              </div>
              <div className="p-3 rounded border">
                <p className="text-xs text-gray-500">En traitement</p>
                <p className="font-semibold">{overview.jobs?.processing ?? 0}</p>
              </div>
              <div className="p-3 rounded border">
                <p className="text-xs text-gray-500">Terminés</p>
                <p className="font-semibold">{overview.jobs?.done ?? 0}</p>
              </div>
              <div className="p-3 rounded border">
                <p className="text-xs text-gray-500">Échoués</p>
                <p className="font-semibold">{overview.jobs?.failed ?? 0}</p>
              </div>
            </div>

            {overview.marketing_automations && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Automations marketing actives</p>
                    <p className="text-xs text-gray-500">
                      {overview.marketing_automations.totals?.active_rules ?? 0} règle(s) actives,
                      {' '}{overview.marketing_automations.totals?.pending_jobs ?? 0} job(s) planifié(s)
                    </p>
                  </div>
                  <div className="text-xs text-gray-500">
                    Envoyés : <span className="font-semibold text-gray-900">{overview.marketing_automations.totals?.sent ?? 0}</span>
                    {' '}• Échecs : <span className="font-semibold text-gray-900">{overview.marketing_automations.totals?.failed ?? 0}</span>
                    {' '}• Push envoyés : <span className="font-semibold text-gray-900">{overview.marketing_automations.totals?.push_sent ?? 0}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(overview.marketing_automations.rules || []).map((rule) => (
                    <div key={rule.id} className="rounded border bg-white p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{rule.label}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Trigger: <span className="font-medium text-gray-700">{rule.trigger_event}</span>
                            {' '}• Délai: <span className="font-medium text-gray-700">{rule.delay_label}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Canaux: <span className="font-medium text-gray-700">{(rule.channels || []).join(' + ') || 'email'}</span>
                          </p>
                        </div>
                        <span className="rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                          {rule.priority}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                        <div className="rounded bg-gray-50 p-2">
                          <p className="text-gray-500">Envoyés</p>
                          <p className="font-semibold text-gray-900">{rule.sent ?? 0}</p>
                        </div>
                        <div className="rounded bg-gray-50 p-2">
                          <p className="text-gray-500">Échecs</p>
                          <p className="font-semibold text-gray-900">{rule.failed ?? 0}</p>
                        </div>
                        <div className="rounded bg-gray-50 p-2">
                          <p className="text-gray-500">Ignorés</p>
                          <p className="font-semibold text-gray-900">{rule.skipped ?? 0}</p>
                        </div>
                        <div className="rounded bg-gray-50 p-2">
                          <p className="text-gray-500">En file</p>
                          <p className="font-semibold text-gray-900">{rule.pending_jobs ?? 0}</p>
                        </div>
                      </div>
                      {!!(rule.channels || []).includes('push') && (
                        <p className="mt-3 text-xs text-gray-500">
                          Push envoyés : <span className="font-semibold text-gray-900">{rule.push_sent ?? 0}</span>
                          {' '}• Push en échec : <span className="font-semibold text-gray-900">{rule.push_failed ?? 0}</span>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => loadAutomationOverview()}>
                <RefreshCcw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
              <Button variant="outline" onClick={processAutomationJobsNow} disabled={processingJobs}>
                <Send className="w-4 h-4 mr-2" />
                {processingJobs ? 'Traitement...' : 'Traiter les jobs en attente'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="brevo" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="brevo">Brevo</TabsTrigger>
          <TabsTrigger value="fcm">FCM</TabsTrigger>
          <TabsTrigger value="maps">Google Maps</TabsTrigger>
          <TabsTrigger value="mobile">Mobile Money</TabsTrigger>
          <TabsTrigger value="custom">Personnalisé</TabsTrigger>
        </TabsList>

        <TabsContent value="brevo">
          <Card>
            <CardHeader>
              <CardTitle>Configuration Brevo</CardTitle>
              <CardDescription>
                Provider central pour les emails transactionnels, les OTP, le marketing et la synchronisation CRM.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold">Activer Brevo</p>
                  <p className="text-sm text-gray-600">
                    Utiliser Brevo comme solution centrale pour les emails et les contacts.
                  </p>
                </div>
                <Switch
                  checked={config.brevo_enabled}
                  onCheckedChange={(checked) => setConfig((prev) => ({ ...prev, brevo_enabled: checked }))}
                />
              </div>

              <div>
                <Label>API Key *</Label>
                <Input
                  type="password"
                  value={config.brevo_api_key || ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, brevo_api_key: e.target.value }))}
                  placeholder="xkeysib-xxxxxxxxxxxxx"
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {getSecretHelperText('brevo_api_key', 'Laissez vide pour conserver la clé Brevo existante.')}
                </p>
              </div>

              <div>
                <Label>Nom de l'expéditeur</Label>
                <Input
                  value={config.brevo_sender_name || ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, brevo_sender_name: e.target.value }))}
                  placeholder="Coli Noki"
                />
              </div>

              <div>
                <Label>Email de l'expéditeur *</Label>
                <Input
                  type="email"
                  value={config.brevo_sender_email || ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, brevo_sender_email: e.target.value }))}
                  placeholder="noreply@colinoki.com"
                />
              </div>

              {config.brevo_enabled && (!isSecretConfigured('brevo_api_key') || !config.brevo_sender_email) && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Brevo est activé mais la configuration est incomplète. Les envois transactionnels et la synchronisation CRM ne pourront pas aboutir.
                  </p>
                </div>
              )}

              {config.brevo_enabled && isSecretConfigured('brevo_api_key') && config.brevo_sender_email && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Brevo est configuré dans Coli Noki. Lancez un diagnostic réel pour confirmer l'accès transactionnel et CRM depuis cet environnement.
                  </p>
                </div>
              )}

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => runProviderTest('brevo')} disabled={providerTestLoading.brevo}>
                  <Send className="w-4 h-4 mr-2" />
                  {providerTestLoading.brevo ? 'Test en cours...' : 'Tester Brevo'}
                </Button>
              </div>

              {providerDiagnostics.brevo && (
                <div className={`space-y-4 p-4 rounded-lg border ${getDiagnosticBadgeClasses(providerDiagnostics.brevo.overall_status)}`}>
                  <div className="flex items-start gap-2">
                    {providerDiagnostics.brevo.overall_status === 'ok' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <p className="font-semibold">Diagnostic Brevo</p>
                      <p className="text-sm">{providerDiagnostics.brevo.summary}</p>
                      {providerDiagnostics.brevo.tested_at && (
                        <p className="text-xs mt-1 opacity-80">
                          Testé le {formatDiagnosticDate(providerDiagnostics.brevo.tested_at)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="p-3 rounded border bg-white/70">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Connexion API</p>
                      <p className="text-sm font-semibold">{getCapabilityStateLabel(providerDiagnostics.brevo.capabilities?.connection)}</p>
                      <p className="text-xs text-gray-600 mt-1">{providerDiagnostics.brevo.capabilities?.connection?.message || 'Diagnostic indisponible'}</p>
                    </div>
                    <div className="p-3 rounded border bg-white/70">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Transactionnel</p>
                      <p className="text-sm font-semibold">{getCapabilityStateLabel(providerDiagnostics.brevo.capabilities?.transactional)}</p>
                      <p className="text-xs text-gray-600 mt-1">{providerDiagnostics.brevo.capabilities?.transactional?.message || 'Diagnostic indisponible'}</p>
                    </div>
                    <div className="p-3 rounded border bg-white/70">
                      <p className="text-xs uppercase tracking-wide text-gray-500">CRM / contacts</p>
                      <p className="text-sm font-semibold">{getCapabilityStateLabel(providerDiagnostics.brevo.capabilities?.crm)}</p>
                      <p className="text-xs text-gray-600 mt-1">{providerDiagnostics.brevo.capabilities?.crm?.message || 'Diagnostic indisponible'}</p>
                    </div>
                    <div className="p-3 rounded border bg-white/70">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Marketing / newsletter</p>
                      <p className="text-sm font-semibold">{getCapabilityStateLabel(providerDiagnostics.brevo.capabilities?.marketing)}</p>
                      <p className="text-xs text-gray-600 mt-1">{providerDiagnostics.brevo.capabilities?.marketing?.message || 'Diagnostic indisponible'}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fcm">
          <Card>
            <CardHeader>
              <CardTitle>Configuration FCM</CardTitle>
              <CardDescription>
                Configure Firebase Cloud Messaging comme canal push mobile pour les notifications transactionnelles et les automations utiles.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold">Activer FCM</p>
                  <p className="text-sm text-gray-600">
                    Active le provider push utilisé par les tokens device, les notifications transactionnelles et les automations mobiles pertinentes.
                  </p>
                </div>
                <Switch
                  checked={config.fcm_enabled}
                  onCheckedChange={(checked) => setConfig((prev) => ({ ...prev, fcm_enabled: checked }))}
                />
              </div>

              <div>
                <Label>Project ID *</Label>
                <Input
                  value={config.fcm_project_id || ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, fcm_project_id: e.target.value }))}
                  placeholder="coli-noki-prod"
                />
              </div>

              <div>
                <Label>Client email *</Label>
                <Input
                  type="email"
                  value={config.fcm_client_email || ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, fcm_client_email: e.target.value }))}
                  placeholder="firebase-adminsdk@coli-noki-prod.iam.gserviceaccount.com"
                />
              </div>

              <div>
                <Label>Private key *</Label>
                <Input
                  type="password"
                  value={config.fcm_private_key || ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, fcm_private_key: e.target.value }))}
                  placeholder="-----BEGIN PRIVATE KEY-----"
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {getSecretHelperText('fcm_private_key', 'Laissez vide pour conserver la clé privée FCM existante.')}
                </p>
              </div>

              <div>
                <Label>Sender ID</Label>
                <Input
                  value={config.fcm_sender_id || ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, fcm_sender_id: e.target.value }))}
                  placeholder="123456789012"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optionnel pour l’instant. Utile plus tard pour certains flux de notifications mobiles.
                </p>
              </div>

              {config.fcm_enabled && (!config.fcm_project_id || !config.fcm_client_email || !isSecretConfigured('fcm_private_key')) && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    FCM est activé mais la configuration est incomplète. Aucun push mobile ne sera envoyé tant que les identifiants de base ne sont pas renseignés.
                  </p>
                </div>
              )}

              {config.fcm_enabled && config.fcm_project_id && config.fcm_client_email && isSecretConfigured('fcm_private_key') && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-800">
                    FCM est prêt côté configuration. Les tokens device et les notifications push transactionnelles peuvent maintenant s’appuyer sur cette base.
                  </p>
                </div>
              )}

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Cette section prépare uniquement le provider FCM. Les devices Flutter, les tokens utilisateurs et les notifications push réelles seront ajoutés dans un chantier séparé.
                </p>
              </div>

              <div className="p-4 bg-gray-50 border rounded-lg">
                <p className="text-sm text-gray-700">
                  Tokens actifs connus : <span className="font-semibold">{overview?.push_device_tokens_active ?? overview?.providers?.fcm?.active_device_tokens ?? 0}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="maps">
          <Card>
            <CardHeader>
              <CardTitle>Configuration Google Maps</CardTitle>
              <CardDescription>
                API pour le calcul de distance et la géolocalisation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold">Activer Google Maps</p>
                  <p className="text-sm text-gray-600">Calcul automatique des distances</p>
                </div>
                <Switch
                  checked={config.google_maps_enabled}
                  onCheckedChange={(checked) => setConfig((prev) => ({ ...prev, google_maps_enabled: checked }))}
                />
              </div>

              <div>
                <Label>API Key *</Label>
                <Input
                  type="password"
                  value={config.google_maps_api_key || ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, google_maps_api_key: e.target.value }))}
                  placeholder="AIzaSy..."
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {getSecretHelperText('google_maps_api_key', "L'API Distance Matrix doit être activée.")}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mobile">
          <Card>
            <CardHeader>
              <CardTitle>Configuration Mobile Money</CardTitle>
              <CardDescription>
                Numéros de réception et configuration API locale.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Cette zone reste disponible pour les opérateurs Mobile Money et d'autres providers métier.
              </p>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Les numéros Mobile Money sont configurés dans les paramètres de l'entreprise. Cette rubrique est conservée pour accueillir plus tard des intégrations opérateur dédiées.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="custom">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Processeurs personnalisés</CardTitle>
                  <CardDescription>
                    Ajoutez vos propres intégrations techniques. Cette zone reste ouverte pour d'autres providers métier ou internes au-delà de Brevo, FCM et Google Maps.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowAddProcessor(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {customProcessors.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun processeur personnalisé configuré</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {customProcessors.map((processor) => (
                    <div key={processor.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold">{processor.name}</h3>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setCustomProcessors((prev) => prev.filter((p) => p.id !== processor.id))}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Slug : {processor.slug}</p>
                      <p className="text-sm text-gray-600">
                        {Object.keys(processor.api_keys).length} clé(s) configurée(s)
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showAddProcessor} onOpenChange={setShowAddProcessor}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajouter un processeur personnalisé</DialogTitle>
            <DialogDescription>
              Configurez un nouveau provider technique sans figer l'architecture pour la suite.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nom du processeur *</Label>
              <Input
                value={newProcessor.name}
                onChange={(e) => setNewProcessor((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Flutterwave, PayPal, FCM"
              />
            </div>

            <div>
              <Label>Slug (identifiant unique) *</Label>
              <Input
                value={newProcessor.slug}
                onChange={(e) => setNewProcessor((prev) => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                placeholder="Ex: flutterwave, paypal, fcm"
              />
            </div>

            <div>
              <Label>Clés API</Label>
              <div className="space-y-2 mt-2">
                {Object.entries(newProcessor.api_keys).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <Input value={key} disabled className="flex-1" />
                    <Input value={value} disabled className="flex-1" />
                    <Button variant="destructive" size="icon" onClick={() => removeCustomKey(key)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mt-3">
                <Input
                  placeholder="Nom de la clé"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
                <Input
                  placeholder="Valeur"
                  value={newKeyValue}
                  onChange={(e) => setNewKeyValue(e.target.value)}
                />
                <Button onClick={addCustomKey}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProcessor(false)}>
              Annuler
            </Button>
            <Button onClick={saveCustomProcessor}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default APIConfiguration;
